import React from 'react';
import moment from "moment";

import { Grid } from '@material-ui/core';

import NonFieldErrors from '../../../../../components/AssetModal/components/NonFieldErrors';
import {decimalStrToNumber} from "../../../../../utils/utils";
import SelectFormElementV2
  from "../../../../RiskProfiling/components/StepContent/components/formElement/SelectFormElementV2";
import InputFormElement
  , {CurrencyNumberFormat} from "../../../../RiskProfiling/components/StepContent/components/formElement/InputFormElement";
import CalendarFormElement
  from "../../../../RiskProfiling/components/StepContent/components/formElement/CalendarFormElement";
import {newDesignInputCustomClasses} from "../../../../RiskProfiling/constants";
import {
  CALL_MONEY_CATEGORY,
  newDesignInputCustomClassesViewOnly
} from "../../../constants";
import {PercentageNumberFormat} from "../../../../RiskProfiling/components/StepContent/components/formElement/InputFormElement/InputFormElement";
import {
  calculateAssetsPayment,
  choiceValue,
  dateToMoment,
  getCalculationDisabledReason,
  momentToDate
} from "../../utils";
import AutoCalculateToggle from "../AutoCalculateToggle";
import useModelWithAutoCalculatedField from "../hooks/useModelWithAutoCalculatedField";
import {checkExternalContractsOption} from "../../../utils";
import {END_DATE_SHOULD_BE_AFTER_START} from "../../../../Trades/constants";


const INTEREST_PERIODS = {
  monthly: {
    id: 1,
    value: "1",
    label: 'Monatlich'
  },
  yearly: {
    id: 2,
    value: "2",
    label: 'Jährlich'
  },
  end_of_term: {
    id: 3,
    value: "3",
    label: 'Endfällig'
  }
};

const INTEREST_PERIODS_DAYS = {
  [INTEREST_PERIODS.monthly.value]: 30,
  [INTEREST_PERIODS.yearly.value]: 360,
};

const INTEREST_PERIODS_OPTIONS = Object.values(INTEREST_PERIODS);

function diffYMD(value) {
  const from = moment().startOf('d');
  const to = value.startOf('d');

  if(from.isAfter(to)){
    return null;
  }

  const years = to.diff(from, 'years');
  from.add(years, 'years');

  const months = to.diff(from, 'months');
  from.add(months, 'months');

  const days = to.diff(from, 'days');

  return [years && `${years}J`, months && `${months}M`, days && `${days}T`]
    .filter(Boolean)
    .join(' ');
}

const cashAutoCalculationHelper = {
  fieldName: 'interests_amount',
  getCalculationDeps: (model) => [
    model.acquisition_date, model.final_duration, model.interests_period, model.interests, model.value
  ],
  getInitialModel: (defaults) => ({
    sub_category: choiceValue(defaults.sub_category),
    name: defaults.name,
    bank_name: defaults.bank_name,
    acquisition_date: dateToMoment(defaults.acquisition_date),
    final_duration: dateToMoment(defaults.final_duration),
    value: decimalStrToNumber(defaults.value),
    interests_period: choiceValue(defaults.interests_period),
    interests_amount: defaults.interests_amount,
    interests:  decimalStrToNumber(defaults.interests),
    updated: dateToMoment(defaults.updated),
  }),
  depsFields: ['acquisition_date', 'final_duration', 'interests_period', 'interests', 'value'],
  isAutoCalcAvailable: (model) => model.acquisition_date && model.final_duration && model.interests_period && model.interests && model.value,
  calculateFieldValue: (model) => {
    return calculateAssetsPayment(
      INTEREST_PERIODS_DAYS[model.interests_period], model.acquisition_date, model.final_duration, model.interests,
      model.value
    );
  }
};

const START_DATE_LABEL = 'Erwerbsdatum';
const END_DATE_LABEL = 'Laufzeit bis';
const VALUE_LABEL = "Anlagesumme / Betrag";
const INTEREST_PERIOD_LABEL = 'Zinsfrequenz';
const INTERESTS_LABEL = 'Zinssatz';

const AUTO_CALC_DISABLED_INFO = getCalculationDisabledReason([
  VALUE_LABEL, START_DATE_LABEL, END_DATE_LABEL, INTEREST_PERIOD_LABEL, INTERESTS_LABEL,
]);

const SUB_CATEGORIES = CALL_MONEY_CATEGORY.subCategories.map(checkExternalContractsOption);

const CashForm = (props) => {

  const {
    handleFormChange,
    defaults,
    errors,
    viewOnly
  } = props;

  const [model, handleModelChange, currentInterestAmount, isAutoCalcAvailable] = useModelWithAutoCalculatedField(
    cashAutoCalculationHelper, defaults
  );

  const timeTillExpiration = React.useMemo(() => {
    return model.final_duration ? diffYMD(model.final_duration) : '';
  }, [model.final_duration]);

  React.useEffect(() => {
    handleFormChange && handleFormChange({
      ...model,
      interests_amount: currentInterestAmount,
      acquisition_date: momentToDate(model.acquisition_date),
      final_duration: momentToDate(model.final_duration),
    });
  }, [model, currentInterestAmount]);

  const inputClasses = viewOnly ? newDesignInputCustomClassesViewOnly : newDesignInputCustomClasses;

  return (
    <>
      {!viewOnly && (
        <Grid item xs={12}>
          <SelectFormElementV2
            label="Kategorie"
            dataId={'category-input'}
            value={model.sub_category}
            options={SUB_CATEGORIES}
            onChange={handleModelChange('sub_category')}
            disabled={viewOnly}
            required
            newDesign
          />
        </Grid>
      )}
      <Grid item xs={12} sm={6}>
        <InputFormElement
          label={'Bezeichnung / Kontonummer'}
          required={!viewOnly}
          dataId={'name-input'}
          value={model.name}
          error={errors.name}
          onChange={handleModelChange('name')}
          disabled={viewOnly}
          custom_classes={inputClasses}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <InputFormElement
          label={'Gesellschaft'}
          dataId={'company_name-input'}
          value={model.bank_name}
          error={errors.bank_name}
          onChange={handleModelChange('bank_name')}
          disabled={viewOnly}
          custom_classes={inputClasses}
        />
      </Grid>
      <Grid item xs={12} sm={3}>
        <InputFormElement
          label={VALUE_LABEL}
          inputComponent={CurrencyNumberFormat}
          inputProps={{inputProps: {allowNegative: false}}}
          value={model.value}
          error={errors.value}
          dataId={'value-input'}
          onChange={handleModelChange('value')}
          required={!viewOnly}
          disabled={viewOnly}
          custom_classes={inputClasses}
        />
      </Grid>
      <Grid item xs={12} sm={3}>
        <CalendarFormElement
          label={START_DATE_LABEL}
          value={model.acquisition_date}
          error={errors.acquisition_date}
          dataId={'acquisition_date-input'}
          onChange={handleModelChange('acquisition_date')}
          disabled={viewOnly}
          custom_classes={inputClasses}
        />
      </Grid>
      <Grid item xs={12} sm={3}>
        <CalendarFormElement
          label={END_DATE_LABEL}
          minDate={model.acquisition_date || undefined}
          minError={END_DATE_SHOULD_BE_AFTER_START}
          value={model.final_duration}
          error={errors.final_duration}
          dataId={'final_duration-input'}
          onChange={handleModelChange('final_duration')}
          disabled={viewOnly}
          custom_classes={inputClasses}
        />
      </Grid>
      <Grid item xs={12} sm={3}>
        <InputFormElement
          label={'Restlaufzeit'}
          value={timeTillExpiration}
          dataId={'till_expiration-input'}
          disabled
          helperText={
            timeTillExpiration === null
              ? "Laufzeitende erreicht"
              : undefined
          }
          custom_classes={inputClasses}
        />
      </Grid>
      <Grid item xs={12} sm={3}>
        <SelectFormElementV2
          label={INTEREST_PERIOD_LABEL}
          dataId={'interest-period-input'}
          value={model.interests_period}
          options={INTEREST_PERIODS_OPTIONS}
          onChange={handleModelChange('interests_period')}
          disabled={viewOnly}
          custom_classes={inputClasses}
          newDesign
          addEmptyOption
        />
      </Grid>
      <Grid item xs={12} sm={3}>
        <InputFormElement
          label={INTERESTS_LABEL}
          inputComponent={PercentageNumberFormat}
          inputProps={{inputProps: {allowNegative: false, isAllowed: (values) => {
            const {floatValue} = values;
            return !floatValue || floatValue <= 100;
          }}}}
          value={model.interests}
          error={errors.interests}
          dataId={'interests-input'}
          onChange={handleModelChange('interests')}
          disabled={viewOnly}
          custom_classes={inputClasses}
        />
      </Grid>
      <Grid item xs={12} sm={3}>
        <Grid container spacing={1}>
          {/*FIXME: DISABLED FOR NOW as part of BCA-10671*/}
          {/*{!viewOnly && (*/}
          {/*  <Grid item xs={'auto'}>*/}
          {/*    <AutoCalculateToggle*/}
          {/*      value={model.is_auto_calculation}*/}
          {/*      handleValueChanged={handleModelChange('is_auto_calculation')}*/}
          {/*      disabled={!isAutoCalcAvailable}*/}
          {/*      disableReason={!isAutoCalcAvailable && AUTO_CALC_DISABLED_INFO}*/}
          {/*    />*/}
          {/*  </Grid>*/}
          {/*)}*/}
          <Grid item xs>
            <InputFormElement
              label="Zinszahlungen"
              inputComponent={CurrencyNumberFormat}
              inputProps={{inputProps: {allowNegative: false}}}
              value={currentInterestAmount}
              error={errors.interests_amount}
              onChange={handleModelChange('interests_amount')}
              dataId={'interests_amount-input'}
              disabled={viewOnly || model.is_auto_calculation}
              custom_classes={inputClasses}
            />
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12} sm={3}>
        <CalendarFormElement
          label="Stand vom"
          value={model.updated || moment()}
          dataId={'updated-input'}
          disabled
          required={!viewOnly}
          custom_classes={inputClasses}
        />
      </Grid>
      {!!errors['non_field_errors'] && <NonFieldErrors errors={errors['non_field_errors']}/>}
    </>
  )
};

CashForm.defaultProps = {
  defaults: {},
  errors: {}
};

export default CashForm;